import { useEffect, useState } from 'react';
import { QuestionWrapper, useFormField, type Group } from '@stepform';

export interface SplitQuantityFieldSchema {
  type: 'SplitQuantity';
  id: string;
  label?: string;
  optional?: boolean;
  helpText?: string;
  supportText?: string;
  min?: number;
  max?: number;
  minTotal?: number;
}

function parseAnswer(answer: unknown): [number, number] {
  if (Array.isArray(answer) && answer.length >= 2) {
    return [Number(answer[0] ?? 0), Number(answer[1] ?? 0)];
  }

  return [0, 0];
}

export function SplitQuantityField({
  fieldSchema,
  group,
}: {
  fieldSchema: SplitQuantityFieldSchema;
  group: Group;
}) {
  const field = useFormField(fieldSchema as never, group);
  const [employees, setEmployees] = useState<number>(() => parseAnswer(field.answer)[0]);
  const [others, setOthers] = useState<number>(() => parseAnswer(field.answer)[1]);

  useEffect(() => {
    field.setAnswer([employees, others]);
  }, [employees, others, field]);

  if (!field.show) {
    return null;
  }

  return (
    <QuestionWrapper {...field} fieldSchema={fieldSchema as never}>
      <div className="answer-group answer-group--stacked">
        <div className="answer">
          <label className="small-label">
            Employees
            <input
              className="text sm"
              type="number"
              min={fieldSchema.min}
              max={fieldSchema.max}
              name="Employees"
              id="Employees"
              value={employees}
              onChange={(e) => setEmployees(Number(e.target.value))}
            />
          </label>
        </div>
        <div className="answer">
          <label className="small-label">
            Other workers
            <input
              className="text sm"
              type="number"
              min={fieldSchema.min}
              max={fieldSchema.max}
              name="OtherWorkers"
              id="OtherWorkers"
              value={others}
              onChange={(e) => setOthers(Number(e.target.value))}
            />
          </label>
        </div>
      </div>
    </QuestionWrapper>
  );
}

export function formatSplitQuantityAnswer(answer: unknown): string {
  if (!Array.isArray(answer) || answer.length < 2) {
    return '';
  }

  const employees = Number(answer[0]);
  const others = Number(answer[1]);

  if (Number.isNaN(employees) || Number.isNaN(others)) {
    return '';
  }

  return `<strong>Employees:</strong> ${employees.toLocaleString()}<br><strong>Other workers:</strong> ${others.toLocaleString()}`;
}
